import React, { useState, useEffect, useRef, useCallback } from "react";
import { I, COLUMNS } from "../data";
import { useWorkspace } from "../WorkspaceContext";
import { useAuth } from "../AuthContext";
import { http, formatApiError, API } from "../api";
import { useConfirm } from "../components/ConfirmDialog";
import { useUpgrade, FEATURES, readPlanError } from "../UpgradeContext";
import { useCopilot } from "../CopilotContext";
import LockedCard from "../components/LockedCard";
import UpgradeButton from "../components/UpgradeButton";
import VerifiedBadge from "../components/VerifiedBadge";
import { StepReveal, RESEARCH_STEPS } from "../components/StepReveal";
import { Loader2, RefreshCw, ExternalLink, Pencil, Check as CheckIcon } from "lucide-react";

const TABS = [
  { id: "overview",  label: "Overview" },
  { id: "checklist", label: "Checklist" },
  { id: "documents", label: "Documents" },
  { id: "costs",     label: "Costs & Aid" },
  { id: "profs",     label: "Professors" },
  { id: "emails",    label: "Emails" },
  { id: "notes",     label: "Notes" },
];

const DOC_TYPE_OPTIONS = [
  { value: "sop",            label: "Statement of Purpose" },
  { value: "resume",         label: "Resume" },
  { value: "cv",             label: "CV" },
  { value: "cover_letter",   label: "Cover letter" },
  { value: "goal_statement", label: "Goal statement" },
  { value: "lor",            label: "Recommendation letter" },
  { value: "transcript",     label: "Transcript" },
  { value: "essay",          label: "Supplemental essay" },
  { value: "other",          label: "Other" },
];

const docTypeLabel = (v) => DOC_TYPE_OPTIONS.find((o) => o.value === v)?.label || "Other";

const fmtSize = (b) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export default function Detail({ uniId, back }) {
  const ws = useWorkspace();
  const { universities, updateUniversity, deleteUniversity, addEmail,
          gmail, connectGmail, draftToGmail, sendViaGmail,
          backfillUniversity, computeCostAndAid, syncChecklistTasks } = ws;
  const { user } = useAuth();
  const confirm = useConfirm();
  const { openCopilot } = useCopilot();
  const u = universities.find((x) => x.id === uniId);
  const [tab, setTab] = useState("overview");

  // -------- Professors (persistent on the uni doc) --------
  const [profs, setProfs] = useState([]);
  const [profsBusy, setProfsBusy] = useState(false);
  const [profsErr, setProfsErr] = useState("");
  const [profsStatus, setProfsStatus] = useState("");
  const profsTimerRef = useRef(null);
  const profileMajor = user?.profile?.intended_major || "";
  const [interest, setInterest] = useState("");
  const [department, setDepartment] = useState("");

  // -------- Email composer --------
  const [drafting, setDrafting] = useState(false);
  const [emailDraft, setEmailDraft] = useState({ to: "", subject: "", body: "", professor_name: "" });
  const [gmailBusy, setGmailBusy] = useState("");
  const [gmailMsg, setGmailMsg] = useState("");
  const [gmailDraftId, setGmailDraftId] = useState(null);
  const [emailDrawerOpen, setEmailDrawerOpen] = useState(false);

  // -------- Notes --------
  const [notes, setNotes] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSavedAt, setNotesSavedAt] = useState(null);

  // Reset all per-university state when uniId changes
  useEffect(() => {
    if (!u) return;
    setTab("overview");
    setProfs(Array.isArray(u.professors) ? u.professors : []);
    setProfsErr("");
    setProfsBusy(false);
    setInterest(profileMajor ? `${profileMajor} — research areas of interest` : "research areas of interest");
    setDepartment(u.program || profileMajor || "");
    setEmailDraft({ to: "", subject: "", body: "", professor_name: "" });
    setDrafting(false);
    setGmailMsg("");
    setGmailDraftId(null);
    setNotes(u.notes || "");
  }, [u?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------- Auto-backfill rich research data for onboarding-seeded unis --------
  const backfillTriggered = useRef(new Set());
  useEffect(() => {
    if (!u) return;
    if (["ready", "pending", "failed"].includes(u.research_status)) return;
    if (backfillTriggered.current.has(u.id)) return;
    backfillTriggered.current.add(u.id);
    backfillUniversity(u.id).catch((err) =>
      console.warn("[detail] background backfill failed:", err)
    );
  }, [u?.id, u?.research_status, u?.fit_notes, u?.highlights, backfillUniversity]); // eslint-disable-line

  // -------- Poll for background research completion --------
  // When a uni is freshly added (or after an intake-term cascade), the backend marks
  // research_status='pending' and runs a background task that flips it to 'ready' once
  // tuition / deadline / fit_notes / highlights / scholarships populate. We poll the
  // workspace every 6s while pending so the page auto-updates without a refresh.
  useEffect(() => {
    if (!u?.id) return;
    if (u.research_status !== "pending") return;
    const tid = setInterval(() => {
      ws.refresh().catch((err) => console.debug("[detail] poll refresh:", err));
    }, 6000);
    return () => clearInterval(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [u?.id, u?.research_status]);

  // -------- Debounced autosave for notes --------
  useEffect(() => {
    if (!u?.id) return;
    if ((u.notes || "") === notes) return;
    const t = setTimeout(async () => {
      setNotesSaving(true);
      try {
        await updateUniversity(u.id, { notes });
        setNotesSavedAt(Date.now());
      } catch (err) {
        console.error("[notepad] autosave failed:", err);
      } finally {
        setNotesSaving(false);
      }
    }, 900);
    return () => clearTimeout(t);
  }, [notes, u?.id]); // eslint-disable-line

  // -------- Scroll-spy for the sticky jump nav (position-based, robust to tall sections) --------
  useEffect(() => {
    if (!u) return;
    const ids = ["b-ov", "b-todo", "b-cost", "b-prof", "b-docs", "b-notes"];
    const jmap = { "b-ov": "ov", "b-todo": "todo", "b-cost": "cost", "b-prof": "prof", "b-docs": "docs", "b-notes": "notes" };
    let raf = 0;
    const compute = () => {
      raf = 0;
      const jumpEl = document.querySelector(".app2 .ujourney .jump");
      const anchor = (jumpEl ? jumpEl.getBoundingClientRect().bottom : 120) + 14;
      let active = ids[0];
      let spanned = false;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top <= anchor && r.bottom > anchor) { active = id; spanned = true; break; }
      }
      if (!spanned) {
        for (const id of ids) {
          const el = document.getElementById(id);
          if (el && el.getBoundingClientRect().top <= anchor) active = id;
        }
      }
      const j = jmap[active];
      document.querySelectorAll(".app2 .ujourney .jump a").forEach((a) => a.classList.toggle("here", a.dataset.j === j));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(compute); };
    compute();
    // capture phase catches scroll from whichever element actually scrolls (window or inner container)
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    const t = setTimeout(compute, 300);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      clearTimeout(t);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [u?.id]); // eslint-disable-line

  if (!u) return (
    <div data-testid="detail-not-found">
      <button className="btn btn-sm btn-ghost" onClick={back}>← Back</button>
      <div className="empty-state" style={{ marginTop: 24 }}>University not found.</div>
    </div>
  );

  const checklist = u.checklist || [];
  const doneCount = checklist.filter((c) => c.done).length;
  const isResearching = u.research_status === "pending";

  const toggleCheck = async (cid) => {
    const next = checklist.map((c) => (c.id === cid ? { ...c, done: !c.done } : c));
    const allReq = next.filter((c) => c.req);
    const progress = allReq.length ? Math.round((allReq.filter((c) => c.done).length / allReq.length) * 100) : 0;
    await updateUniversity(u.id, { checklist: next, progress });
  };

  const findProfs = async () => {
    setProfsBusy(true); setProfsErr(""); setProfsStatus("Pulling official faculty directories…");
    // Progressive status — keeps the UI feeling alive during the 25-40s grounded call.
    const stages = [
      { at: 8000,  text: "Reading department pages…" },
      { at: 18000, text: "Matching faculty to your interest…" },
      { at: 28000, text: "Verifying lab links on the university domain…" },
      { at: 42000, text: "Still working — this can take a moment for smaller departments." },
    ];
    stages.forEach((s) => {
      const tid = setTimeout(() => setProfsStatus(s.text), s.at);
      profsTimerRef.current = [...(profsTimerRef.current || []), tid];
    });
    try {
      const { data } = await http.post("/ai/find-professors", {
        university_name: u.name,
        research_interest: interest,
        department: department || u.program || profileMajor || "",
      }, { timeout: 75000 });
      const list = data.data || [];
      setProfs(list);
      if (list.length === 0) {
        setProfsErr("Vidhya couldn't verify enough faculty on the university's own domain to show them — try a more specific research interest, or check the department's directory directly.");
      }
      // Persist on the university so they survive a refresh / tab switch.
      updateUniversity(u.id, { professors: list }).catch((err) =>
        console.warn("[profs] persist failed:", err)
      );
    } catch (e) {
      setProfsErr(formatApiError(e));
    } finally {
      (profsTimerRef.current || []).forEach(clearTimeout);
      profsTimerRef.current = [];
      setProfsBusy(false);
      setProfsStatus("");
    }
  };

  const draftWithAi = async (prof) => {
    setDrafting(true);
    setEmailDrawerOpen(true);
    try {
      const target = prof || profs[0];
      const p = user?.profile || {};
      const bgParts = [];
      if (p.grade_level) bgParts.push(p.grade_level);
      if (p.intended_major) bgParts.push(`${p.intended_major} applicant`);
      else if (department) bgParts.push(`${department} applicant`);
      if (p.gpa) bgParts.push(`GPA ${p.gpa}`);
      if (Array.isArray(p.activities) && p.activities.length) {
        bgParts.push(`Activities/projects: ${p.activities.slice(0, 3).join("; ")}`);
      }
      if (p.bio) bgParts.push(p.bio);
      bgParts.push(`Currently exploring: ${interest}.`);
      const studentBackground = bgParts.filter(Boolean).join(". ");
      const programApplying =
        u.program ||
        (target?.department ? `${target.department}` : "") ||
        p.intended_major ||
        department ||
        "the program";

      setEmailDraft({
        to: target?.email || "",
        subject: "",
        body: "",
        professor_name: target?.name || "",
      });

      const token = localStorage.getItem("sa_token");
      const resp = await fetch(`${API}/ai/draft-email/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          professor_name: target?.name || "the admissions team",
          professor_email: target?.email || "",
          professor_research: target?.research || target?.notable_work || "",
          university: u.name,
          student_name: user?.name || p.name || "Student",
          student_background: studentBackground,
          program_applying: programApplying,
          purpose: "research_inquiry",
        }),
      });
      if (!resp.ok || !resp.body) {
        const detail = await resp.text().catch(() => `HTTP ${resp.status}`);
        throw new Error(detail || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const dataLine = rawEvent.split("\n").find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          const jsonStr = dataLine.slice(5).trim();
          if (!jsonStr) continue;
          let evt;
          try { evt = JSON.parse(jsonStr); } catch (err) { continue; }
          if (evt.type === "subject" && evt.text) {
            setEmailDraft((d) => ({ ...d, subject: evt.text }));
          } else if (evt.type === "delta" && evt.text) {
            setEmailDraft((d) => ({ ...d, body: (d.body || "") + evt.text }));
          } else if (evt.type === "done") {
            if (evt.subject) setEmailDraft((d) => ({ ...d, subject: evt.subject }));
            if (evt.body) setEmailDraft((d) => ({ ...d, body: evt.body }));
          } else if (evt.type === "error") {
            throw new Error(evt.detail || "stream error");
          }
        }
      }
    } catch (e) {
      setEmailDraft((d) => ({ ...d, body: `Error drafting: ${e?.message || formatApiError(e)}` }));
    } finally {
      setDrafting(false);
    }
  };

  const saveDraft = async () => {
    if (!emailDraft.body.trim()) return;
    await addEmail({ ...emailDraft, uni_id: u.id });
    setGmailMsg("Saved locally to Vidhya. Connect Gmail to also save it as a real Gmail draft.");
  };

  const openGmailConsent = async () => {
    try {
      const data = await connectGmail();
      const w = window.open(data.url, "_blank", "width=520,height=640");
      const onMsg = (ev) => {
        if (ev.data && ev.data.type === "gmail-connected") {
          window.removeEventListener("message", onMsg);
          setTimeout(() => window.location.reload(), 800);
        }
      };
      window.addEventListener("message", onMsg);
      const poll = setInterval(async () => {
        try {
          const { data: j } = await http.get("/gmail/status");
          if (j.connected) {
            clearInterval(poll);
            window.removeEventListener("message", onMsg);
            window.location.reload();
          }
        } catch (err) {
          console.debug("[gmail] connection poll error:", err);
        }
        if (w && w.closed) clearInterval(poll);
      }, 2000);
    } catch (e) {
      setGmailMsg(formatApiError(e));
    }
  };

  const saveToGmailDrafts = async () => {
    if (!emailDraft.body.trim() || !emailDraft.to.trim() || !emailDraft.subject.trim()) {
      setGmailMsg("To, subject, and body are all required to save a Gmail draft.");
      return;
    }
    setGmailBusy("draft"); setGmailMsg("");
    try {
      const data = await draftToGmail({
        to: emailDraft.to,
        subject: emailDraft.subject,
        body: emailDraft.body,
        university_id: u.id,
        professor: emailDraft.professor_name,
      });
      setGmailDraftId(data.email?.gmail_draft_id || null);
      setGmailMsg(`✓ Saved to your Gmail drafts. Open Gmail to review.`);
    } catch (e) {
      setGmailMsg(formatApiError(e));
    } finally {
      setGmailBusy("");
    }
  };

  const sendNow = async () => {
    if (!gmailDraftId && (!emailDraft.body.trim() || !emailDraft.to.trim() || !emailDraft.subject.trim())) {
      setGmailMsg("Compose first, then send.");
      return;
    }
    const ok = await confirm({
      title: "Send this email?",
      message: `Vidhya will send this email to ${emailDraft.to} from your connected Gmail account.`,
      confirmLabel: "Send via Gmail",
    });
    if (!ok) return;
    setGmailBusy("send"); setGmailMsg("");
    try {
      await sendViaGmail(gmailDraftId
        ? { gmail_draft_id: gmailDraftId }
        : { to: emailDraft.to, subject: emailDraft.subject, body: emailDraft.body }
      );
      setGmailMsg("✓ Sent. Vidhya will pull replies into your tracker automatically.");
      setEmailDraft({ to: "", subject: "", body: "", professor_name: "" });
      setGmailDraftId(null);
    } catch (e) {
      setGmailMsg(formatApiError(e));
    } finally {
      setGmailBusy("");
    }
  };

  const removeUni = async () => {
    const ok = await confirm({
      title: "Remove this university?",
      message: `${u.name} will be removed from your shortlist along with its checklist progress, notes, and documents.`,
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    await deleteUniversity(u.id);
    back();
  };

  const pct = u.progress || 0;
  const ringDash = Math.max(0, 157 - 157 * (pct / 100));
  const tierCls = (t) => {
    const s = (t || "").toLowerCase();
    if (/good|target|match/.test(s)) return "good";
    if (/back|safety/.test(s)) return "back";
    return "amb";
  };
  const jumpTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const isNavigator = !!user?.plan_summary?.is_navigator || user?.plan === "navigator";

  return (
    <div className="vwrap ujourney" data-testid="detail-view">
      <div className="ubackrow">
        <span className="uback" onClick={back} data-testid="detail-back">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
          Back to my universities
        </span>
      </div>

      <div className="uhead" data-testid="detail-hero">
        <div className="big">{(u.short || u.name)[0]}</div>
        <div className="ht">
          <h1>{u.name}{u.tier && <span className={`tier ${tierCls(u.tier)}`}>{u.tier}</span>}{isResearching && <span className="tier amb" data-testid="research-pending">Researching…</span>}</h1>
          <div className="loc">
            {u.loc && <span>{u.loc}</span>}
            {u.acceptRate && <span><b>{u.acceptRate}</b> acceptance</span>}
            {u.program && <span>{u.program}</span>}
            {u.website && <a href={u.website} target="_blank" rel="noreferrer">Official ↗</a>}
          </div>
        </div>
        <div className="ring" title={`${pct}% ready`}>
          <svg width="60" height="60" viewBox="0 0 60 60">
            <circle cx="30" cy="30" r="25" fill="none" stroke="var(--indigo-50)" strokeWidth="7" />
            <circle cx="30" cy="30" r="25" fill="none" stroke="var(--lemon)" strokeWidth="7" strokeLinecap="round" strokeDasharray="157" strokeDashoffset={ringDash} />
          </svg>
          <span className="pct">{pct}%</span><span className="lab">ready</span>
        </div>
        {u.deadline && (
          <div className="deadchip">
            <span className={`d ${u.deadlineUrgent ? "" : "ok"}`}>{u.deadline}</span>
            <span className="l">{u.deadlineUrgent ? "due soon" : "deadline"}</span>
          </div>
        )}
      </div>

      <div className="jump" id="jump">
        <a className="here" data-j="ov" onClick={() => jumpTo("b-ov")}>Overview</a>
        <a data-j="todo" onClick={() => jumpTo("b-todo")}>What to do</a>
        <a data-j="cost" onClick={() => jumpTo("b-cost")}>Costs &amp; aid</a>
        <a data-j="prof" onClick={() => jumpTo("b-prof")}>Professors</a>
        <a data-j="docs" onClick={() => jumpTo("b-docs")}>Documents</a>
        <a data-j="notes" onClick={() => jumpTo("b-notes")}>Notes</a>
        <button
          className="jump-research-more"
          onClick={() => openCopilot("menu", { uni: u.name, uniId: u.id, program: u.program }, {
            preset: `Research more about ${u.name}${u.program ? ` (${u.program})` : ""}. Anything important I should know — recent news, ranking shifts, admissions trends, professor moves, scholarship changes, student outcomes.`,
          })}
          data-testid="research-more-btn"
          title="Open Vidhya and dig deeper"
        >
          <I.Sparkle size={13} /> Research more
        </button>
      </div>

      {/* Overview — expanded fit */}
      <div className="block" id="b-ov" data-testid="block-overview">
        <h3>The fit, in one look</h3>
        <div className="bdesc">Vidhya researched this live. Figures it couldn't byte-verify are labelled, never faked.</div>
        {isResearching && !u.fit_notes && !(u.highlights && u.highlights.length) ? (
          <div style={{ marginTop: 10 }} data-testid="research-progress">
            <div className="bdesc" style={{ marginBottom: 12 }}>Researching this program live — here's what Vidhya is pulling right now.</div>
            <StepReveal steps={RESEARCH_STEPS} active interval={7000} testid="research-steps" />
          </div>
        ) : (
          <div style={{ marginTop: 6 }}>
            <FitSnapshot u={u} isResearching={isResearching} />
          </div>
        )}
      </div>

      {/* What to do */}
      <div className="block" id="b-todo" data-testid="block-todo">
        <h3>What you need to do <span className="connpill"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 12h8M12 8v8" /></svg>also in your Tasks</span></h3>
        <div className="bdesc">Tailored to this program. Check here and it ticks in your task center too.</div>
        {checklist.length > 0 && (
          <div className="pgwrap">
            <span className="pn">{doneCount}/{checklist.length}</span>
            <div className="pgbar"><i style={{ width: `${pct}%` }} /></div>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--slate)", fontWeight: 700 }}>{pct}% ready</span>
          </div>
        )}
        <div className="clist">
          {checklist.length === 0 ? (
            <div className="blockmsg">No checklist items yet for this program.</div>
          ) : (
            checklist.map((c) => (
              <div key={c.id} className={`cl ${c.done ? "done" : ""}`} onClick={() => toggleCheck(c.id)} data-testid={`checklist-item-${c.id}`}>
                <div className="chk"><svg viewBox="0 0 24 24" fill="none"><path d="m5 12 5 5L20 6" /></svg></div>
                <div className="tb"><b>{c.name}</b>{c.req === false && <span>Optional but recommended</span>}</div>
              </div>
            ))
          )}
        </div>
        <div style={{ marginTop: 13 }}>
          <button className="pill-add" onClick={() => syncChecklistTasks?.(u.id)} data-testid="sync-tasks-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>Sync to my tasks
          </button>
        </div>
      </div>

      {/* Costs & aid (existing working logic + verified-tuition guardrail) */}
      <div id="b-cost" style={{ scrollMarginTop: 58 }} data-testid="block-cost">
        <CostsTab u={u} profile={user?.profile} computeCostAndAid={computeCostAndAid} switchTab={setTab} isNavigator={isNavigator} />
      </div>

      {/* Professors */}
      <div className="block" id="b-prof" data-testid="block-prof">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h3>Professors worth reaching out to</h3>
            <div className="bdesc">Found inside this exact department. Verified lab links, match on your interests.</div>
          </div>
          {isNavigator && (
            <button className="pbtn gho" onClick={() => setEmailDrawerOpen(true)} data-testid="open-outreach-drawer">
              <I.Mail size={12} /> Open outreach
              {(ws.emails || []).filter((e) => e.uni_id === u.id || e.uni === u.name).length > 0 && (
                <span className="pill" style={{ marginLeft: 6 }}>{(ws.emails || []).filter((e) => e.uni_id === u.id || e.uni === u.name).length}</span>
              )}
            </button>
          )}
        </div>
        {profsErr && <div className="berr" data-testid="profs-error">{profsErr}</div>}
        {profs.length === 0 ? (
          <div>
            <div className="blockmsg">No professors yet. Vidhya can find faculty in your area, inside this department.</div>
            <button className="pbtn fill" onClick={findProfs} disabled={profsBusy} data-testid="find-profs-btn">
              {profsBusy ? (profsStatus || "Searching…") : "Find professors with Vidhya"}
            </button>
            {profsBusy && (
              <div className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }} data-testid="profs-progress">
                <Loader2 size={12} className="vidhya-spin" style={{ verticalAlign: -2, marginRight: 6 }} />
                {profsStatus || "Pulling official faculty directories…"}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="profrow">
              {profs.map((p, i) => (
                <div className="profcard" key={i} data-testid={`prof-card-${i}`}>
                  <div className="pn">{p.name}{(p.match || p.match_score) && <span className="matchg">{p.match || p.match_score}{typeof (p.match || p.match_score) === "number" ? "% match" : ""}</span>}</div>
                  <div className="pr">{p.title || p.department || ""}</div>
                  {(p.research || p.notable_work) && <div className="pclip">{p.research || p.notable_work}</div>}
                  <div className="pa">
                    <button className="pbtn fill" onClick={() => draftWithAi(p)} data-testid={`prof-draft-${i}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16v16H4z" /><path d="m4 7 8 5 8-5" /></svg>Draft outreach
                    </button>
                    {(p.lab_url || p.profile_url) && <a className="pbtn gho" href={p.lab_url || p.profile_url} target="_blank" rel="noreferrer">Lab ↗</a>}
                    {p.verified && <span className="vbadge" style={{ padding: "3px 8px" }}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6 9 17l-5-5" /></svg>verified</span>}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 13 }}>
              <button className="pill-add" onClick={findProfs} disabled={profsBusy} data-testid="find-more-profs-btn">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>{profsBusy ? (profsStatus || "Searching…") : "Find more"}
              </button>
              {profsBusy && (
                <div className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }} data-testid="profs-progress">
                  <Loader2 size={12} className="vidhya-spin" style={{ verticalAlign: -2, marginRight: 6 }} />
                  {profsStatus || "Pulling official faculty directories…"}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Outreach moved to side drawer — opened from professor cards or this button */}

      {/* Documents (existing upload/download logic) */}
      <div id="b-docs" style={{ scrollMarginTop: 58 }} data-testid="block-docs">
        <DocumentsTab u={u} ws={ws} confirm={confirm} />
      </div>

      {/* Notes */}
      <div className="block" id="b-notes" data-testid="block-notes">
        <div className="notes-head" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <h3>Notes</h3>
            <div className="bdesc">Your private scratchpad for this university.</div>
          </div>
        </div>
        <textarea className="notearea" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Jot anything about this university — replies from professors, fee waivers, reminders…" data-testid="notes-textarea" />
        <div className="notesave" data-testid="notes-status">{notesSaving ? "Saving…" : notesSavedAt ? "Saved" : ""}</div>
      </div>

      <div style={{ marginTop: 18, textAlign: "right" }}>
        <button className="pbtn gho" onClick={removeUni} data-testid="detail-remove-btn">
          <I.Trash size={13} /> Remove from board
        </button>
      </div>
      <div aria-hidden="true" style={{ height: "58vh" }} />

      {/* Side drawer for outreach (email composer + past emails) */}
      <EmailDrawer
        open={emailDrawerOpen}
        onClose={() => setEmailDrawerOpen(false)}
        u={u} gmail={gmail} drafting={drafting} profs={profs}
        emailDraft={emailDraft} setEmailDraft={setEmailDraft}
        gmailBusy={gmailBusy} gmailMsg={gmailMsg}
        draftWithAi={draftWithAi} saveDraft={saveDraft}
        openGmailConsent={openGmailConsent}
        saveToGmailDrafts={saveToGmailDrafts} sendNow={sendNow}
        emailHistory={(ws.emails || []).filter((e) => e.uni_id === u.id || e.uni === u.name)}
        isNavigator={isNavigator}
      />
    </div>
  );
}

// ───────────────────────── Tabs Header ─────────────────────────
function DetailTabs({ current, setCurrent }) {
  return (
    <div className="detail-tabs" data-testid="detail-tabs">
      {TABS.map((t) => (
        <button
          key={t.id}
          className={`detail-tab ${current === t.id ? "active" : ""}`}
          onClick={() => setCurrent(t.id)}
          data-testid={`tab-${t.id}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ───────────────────────── Overview ─────────────────────────
function OverviewTab({ u, isResearching, switchTab }) {
  return (
    <div className="col" style={{ gap: 16 }}>
      <FitSnapshot u={u} isResearching={isResearching} />
      <div className="card" style={{ padding: 18 }} data-testid="overview-quick-cards">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
          <QuickCard label="Checklist" v={`${(u.checklist || []).filter((c) => c.done).length} / ${(u.checklist || []).length}`} onClick={() => switchTab("checklist")} />
          <QuickCard label="Documents" v="Open" onClick={() => switchTab("documents")} />
          <QuickCard label="Professors" v={`${(u.professors || []).length || "Find now"}`} onClick={() => switchTab("profs")} />
          <QuickCard label="Tuition" v={u.tuition_verification?.status === "verified" ? "Verified ✓" : "See Costs tab"} onClick={() => switchTab("costs")} />
        </div>
      </div>
    </div>
  );
}

function QuickCard({ label, v, onClick }) {
  return (
    <button className="overview-quick" onClick={onClick} data-testid={`quick-${label.toLowerCase()}`}>
      <span className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
      <span style={{ fontSize: 18, fontWeight: 600, marginTop: 4 }}>{v}</span>
    </button>
  );
}

// ───────────────────────── Checklist ─────────────────────────
function ChecklistTab({ u, checklist, doneCount, toggle, syncChecklistTasks }) {
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");

  const onSync = async () => {
    setSyncing(true); setSyncMsg("");
    try {
      const data = await syncChecklistTasks(u.id);
      const created = data?.created || 0;
      const skipped = data?.skipped || 0;
      setSyncMsg(
        created === 0
          ? `All ${skipped} items are already in your Task Center.`
          : `✓ Added ${created} new item${created === 1 ? "" : "s"} to your Task Center.`
      );
    } catch (e) {
      setSyncMsg(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="card checklist" data-testid="checklist-card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <h3>Application requirements</h3>
        <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
          <span className="pill">{doneCount} of {checklist.length} complete</span>
          <button
            className="btn btn-sm btn-clay"
            onClick={onSync}
            disabled={syncing || checklist.length === 0}
            data-testid="checklist-sync-btn"
            title="Mirror every item as a task with this university's deadline"
          >
            {syncing ? "Syncing…" : <><I.ArrowRight size={11} /> Send to Task Center</>}
          </button>
        </div>
      </div>
      {syncMsg && (
        <div className="muted" style={{ fontSize: 12.5, marginBottom: 10 }} data-testid="checklist-sync-msg">
          {syncMsg}
        </div>
      )}
      <div style={{ height: 4, borderRadius: 999, background: "var(--bg-sunk)", marginBottom: 14, overflow: "hidden" }}>
        <div style={{ width: `${checklist.length ? (doneCount / checklist.length) * 100 : 0}%`, height: "100%", background: "var(--clay)" }}></div>
      </div>
      {checklist.map((c) => (
        <div className="checklist-item" key={c.id} data-testid={`checklist-${c.id}`}>
          <div className={`check-box ${c.done ? "done" : ""}`} onClick={() => toggle(c.id)}>
            {c.done && <I.Check size={12} />}
          </div>
          <div className="check-text">
            <div className={c.done ? "done" : ""} style={{ fontWeight: 500, fontSize: 13.5 }}>{c.name}</div>
            <div className="check-meta">
              {c.category === "essay" && "Essay"}
              {c.category === "doc" && "Document upload"}
              {c.category === "lor" && "Recommender required"}
              {c.category === "test" && "Score report"}
              {c.category === "payment" && "Payment required"}
              {c.category === "supp" && "Supplemental"}
              {c.category === "interview" && "Interview"}
              {c.category === "portfolio" && "Portfolio"}
              {c.category === "video" && "Video"}
              {!c.req && <span style={{ marginLeft: 6, opacity: 0.6 }}>· optional</span>}
            </div>
          </div>
        </div>
      ))}
      {checklist.length === 0 && <div className="empty-state">No checklist items yet — Vidhya is still researching this program.</div>}
    </div>
  );
}

// ───────────────────────── Documents ─────────────────────────
function DocumentsTab({ u, ws, confirm }) {
  const { universities, listDocuments, uploadDocument, renameDocument, deleteDocument, duplicateDocument } = ws;
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [dupModal, setDupModal] = useState(null); // { doc }
  const [editing, setEditing] = useState(null);   // { docId, name, doc_type }

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listDocuments(u.id);
      setDocs(list);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [u.id, listDocuments]);

  useEffect(() => { reload(); }, [reload]);

  const handleFiles = async (files, docType = "other") => {
    if (!files || !files.length) return;
    setErr(""); setBusy(true);
    try {
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) {
          setErr(`"${f.name}" is over 10 MB. Please upload smaller files.`);
          continue;
        }
        await uploadDocument(u.id, f, { name: f.name, doc_type: docType });
      }
      await reload();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  const onDownload = (doc) => {
    const token = localStorage.getItem("sa_token");
    const url = `${API}/documents/${doc.id}/download?auth=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank", "noopener");
  };

  const onRename = async () => {
    if (!editing) return;
    try {
      await renameDocument(editing.docId, { name: editing.name, doc_type: editing.doc_type });
      setEditing(null);
      await reload();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const onDelete = async (doc) => {
    const ok = await confirm({
      title: "Delete this document?",
      message: `"${doc.name}" will be removed from this university.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteDocument(doc.id);
      await reload();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const onDuplicate = async (targetUniId) => {
    if (!dupModal) return;
    try {
      await duplicateDocument(dupModal.doc.id, targetUniId);
      setDupModal(null);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const otherUnis = universities.filter((x) => x.id !== u.id);

  return (
    <div className="card" data-testid="documents-tab" style={{ padding: 20 }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ margin: 0 }}>Documents</h3>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Upload your SOP, resume, transcripts, recommendations — anything you need for {u.short || u.name}.
            Reuse a document in another application with one click.
          </div>
        </div>
        <button
          className="btn btn-sm btn-clay"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
          data-testid="docs-upload-btn"
        >
          <I.Upload size={12} /> {busy ? "Uploading…" : "Upload file"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid="docs-file-input"
        />
      </div>

      <div
        className={`doc-drop ${dragOver ? "is-dragging" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        data-testid="docs-dropzone"
      >
        <I.Upload size={18} />
        <div style={{ marginTop: 6, fontSize: 13 }}>
          Drag files here or <button className="link-btn" onClick={() => fileInputRef.current?.click()}>browse</button>
        </div>
        <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>PDF, DOCX, TXT up to 10 MB</div>
      </div>

      {err && <div className="auth-error" style={{ marginTop: 12 }} data-testid="docs-error">{err}</div>}

      <div style={{ marginTop: 14 }}>
        {loading && <div className="muted" style={{ fontSize: 12 }}>Loading documents…</div>}
        {!loading && docs.length === 0 && (
          <div className="empty-state" style={{ fontSize: 13 }}>
            No documents uploaded for this university yet. Drop your SOP or resume here to get started.
          </div>
        )}
        {!loading && docs.length > 0 && (
          <div className="doc-list" data-testid="docs-list">
            {docs.map((d) => (
              <div className="doc-row" key={d.id} data-testid={`doc-row-${d.id}`}>
                <div className="doc-icon"><I.Doc size={18} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="doc-name" title={d.name}>{d.name}</div>
                  <div className="doc-meta">
                    <span>{docTypeLabel(d.doc_type)}</span>
                    <span>·</span>
                    <span>{fmtSize(d.size)}</span>
                    <span>·</span>
                    <span>{new Date(d.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="doc-actions">
                  <button className="btn btn-sm btn-ghost" title="View / download" onClick={() => onDownload(d)} data-testid={`doc-dl-${d.id}`}>
                    <I.Download size={12} />
                  </button>
                  <button
                    className="btn btn-sm btn-ghost"
                    title="Copy to another university"
                    onClick={() => setDupModal({ doc: d })}
                    disabled={otherUnis.length === 0}
                    data-testid={`doc-dup-${d.id}`}
                  >
                    <I.Copy size={12} />
                  </button>
                  <button
                    className="btn btn-sm btn-ghost"
                    title="Rename / change type"
                    onClick={() => setEditing({ docId: d.id, name: d.name, doc_type: d.doc_type })}
                    data-testid={`doc-edit-${d.id}`}
                  >
                    <I.Settings size={12} />
                  </button>
                  <button
                    className="btn btn-sm btn-ghost"
                    title="Delete"
                    onClick={() => onDelete(d)}
                    data-testid={`doc-del-${d.id}`}
                  >
                    <I.Trash size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {dupModal && (
        <Modal onClose={() => setDupModal(null)} title={`Copy "${dupModal.doc.name}" to…`} testid="dup-modal">
          {otherUnis.length === 0 ? (
            <div className="muted">Add a second university first.</div>
          ) : (
            <div className="dup-list">
              {otherUnis.map((x) => (
                <button key={x.id} className="dup-row" onClick={() => onDuplicate(x.id)} data-testid={`dup-target-${x.id}`}>
                  <div className="uni-mark uni-mark-sm" style={{ background: x.color }}>
                    {(x.short || x.name).slice(0, 2)}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                    <span style={{ fontWeight: 600, fontSize: 13.5 }}>{x.name}</span>
                    <span className="muted" style={{ fontSize: 11 }}>{x.program}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Modal>
      )}

      {editing && (
        <Modal onClose={() => setEditing(null)} title="Edit document" testid="edit-modal">
          <label className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>Name</label>
          <input
            className="input"
            value={editing.name}
            onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
            style={{ marginTop: 4, marginBottom: 12 }}
            data-testid="edit-doc-name"
          />
          <label className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>Type</label>
          <select
            className="input"
            value={editing.doc_type}
            onChange={(e) => setEditing((p) => ({ ...p, doc_type: e.target.value }))}
            style={{ marginTop: 4 }}
            data-testid="edit-doc-type"
          >
            {DOC_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div className="row" style={{ marginTop: 16, justifyContent: "flex-end", gap: 8 }}>
            <button className="btn btn-sm" onClick={() => setEditing(null)}>Cancel</button>
            <button className="btn btn-sm btn-clay" onClick={onRename} data-testid="edit-doc-save">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ───────────────────────── Verified fact (3-tier) ─────────────────────────
// Generic figure used for both Tuition and Deadline. Shows:
//  • verified   → value + green badge (+ snippet)
//  • found      → value (neutral) + "confirm on official page" link
//  • not_found  → explore link + an editable field the student can fill in (persisted)
//  • self_reported → the student's own value shown plainly (no "self-reported" label), editable
function FactFigure({ u, factType, title, cacheKey, endpoint, autoload = true, transformValue = null }) {
  const cached = u[cacheKey] || null;
  const [loading, setLoading] = useState(autoload && !cached);
  const [fv, setFv] = useState(cached);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const pull = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.post(`/universities/${u.id}/${endpoint}`, null, { timeout: 55000 });
      setFv(r.data?.data || null);
    } catch (e) {
      setFv(null);
    } finally {
      setLoading(false);
    }
  }, [u.id, endpoint]);

  useEffect(() => {
    if (autoload && !cached) pull();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [u.id]);

  const saveSelf = async () => {
    const value = (draft || "").trim();
    if (!value) { setEditing(false); return; }
    setSaving(true);
    try {
      const r = await http.post(`/universities/${u.id}/self-fact`, { fact_type: factType, value });
      setFv(r.data?.data || null);
      setEditing(false);
      setDraft("");
    } catch (e) { /* keep editing on failure */ }
    finally { setSaving(false); }
  };

  const status = fv?.status;
  const verified = status === "verified" && fv?.value && fv?.source_url;
  const found = status === "found" && fv?.value;
  const selfReported = status === "self_reported" && fv?.value;
  const exploreUrl = fv?.explore_url || fv?.verify_url || fv?.source_url || "";

  const Explore = exploreUrl ? (
    <a href={exploreUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm"
       data-testid={`${factType}-explore-link`} style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
      Explore official page <ExternalLink size={12} />
    </a>
  ) : null;

  const Editor = (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
      <input
        className="vd-self-input"
        data-testid={`${factType}-self-input`}
        value={draft}
        autoFocus
        placeholder={factType === "tuition" ? "e.g. $58,240 / year" : "e.g. December 15, 2026"}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") saveSelf(); if (e.key === "Escape") setEditing(false); }}
        style={{ padding: "7px 10px", border: "1px solid var(--rule)", borderRadius: 8, fontSize: 14, minWidth: 180 }}
      />
      <button className="btn btn-sm" onClick={saveSelf} disabled={saving} data-testid={`${factType}-self-save`}>
        {saving ? <Loader2 size={12} className="vidhya-spin" /> : <CheckIcon size={12} />} Save
      </button>
      <button className="btn btn-sm btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
    </div>
  );

  return (
    <div data-testid={`${factType}-figure`}
         style={{ border: "1px solid var(--rule)", borderRadius: 14, padding: "14px 16px", background: "var(--paper-2)" }}>
      <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>{title}</div>

      {loading && (
        <div data-testid={`${factType}-loading`} style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
          <Loader2 size={14} className="vidhya-spin" />
          <span style={{ fontSize: 14, color: "var(--ink-2)" }}>Pulling live {factType} from the official source…</span>
        </div>
      )}

      {!loading && (verified || found || selfReported) && (
        <div style={{ marginTop: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div data-testid={`${factType}-value`} style={{ fontWeight: 700, fontSize: 22 }}>{transformValue ? transformValue(fv.value) : fv.value}</div>
            {verified && <VerifiedBadge status="verified" label={`${title} verified`} source={fv.source_url} checkedAt={fv.fetched_at} />}
            {found && <VerifiedBadge status="found" source={fv.source_url} />}
            {!verified && (
              <button className="vd-edit-btn" onClick={() => { setDraft(fv.value || ""); setEditing(true); }}
                      data-testid={`${factType}-edit-btn`} title="Edit this value"
                      style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--muted)", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                <Pencil size={12} /> Edit
              </button>
            )}
          </div>
          {verified && fv.snippet && !/\|[^|]*\|[^|]*\|/.test(fv.snippet) && (
            <div className="muted" style={{ fontSize: 12, marginTop: 6, fontStyle: "italic", lineHeight: 1.5 }}>“{fv.snippet}”</div>
          )}
          {found && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
              {Explore}
              <button className="btn btn-sm btn-ghost" onClick={pull} data-testid={`${factType}-recheck-btn`}>
                <RefreshCw size={11} /> Re-check
              </button>
            </div>
          )}
          {editing && Editor}
        </div>
      )}

      {!loading && !verified && !found && !selfReported && !fv && (
        <div data-testid={`${factType}-idle`} style={{ marginTop: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            {u.deadline && factType === "deadline" && (
              <div style={{ fontWeight: 600, fontSize: 16, color: "var(--ink-2)" }}>{u.deadline}</div>
            )}
            <button className="btn btn-sm" onClick={pull} data-testid={`${factType}-verify-btn`}>
              <RefreshCw size={11} /> Verify {factType} with Vidhya
            </button>
          </div>
        </div>
      )}

      {!loading && !verified && !found && !selfReported && fv && (
        <div data-testid={`${factType}-notfound`} style={{ marginTop: 8 }}>
          <div style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.5 }}>
            We couldn't confirm {factType === "tuition" ? "a tuition figure" : "a deadline"} on the official
            source right now — explore the page yourself, or add it below.
          </div>
          {!editing && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
              {Explore}
              <button className="btn btn-sm" onClick={() => { setDraft(""); setEditing(true); }} data-testid={`${factType}-add-btn`}>
                <Pencil size={11} /> Add it yourself
              </button>
              <button className="btn btn-sm btn-ghost" onClick={pull} data-testid={`${factType}-recheck-btn`}>
                <RefreshCw size={11} /> Re-check
              </button>
            </div>
          )}
          {editing && Editor}
        </div>
      )}
    </div>
  );
}

// ───────────────────────── Costs & Aid ─────────────────────────
// Domestic (in-state) vs international (out-of-state) tuition tier, decided by the
// student's country/citizenship vs the university's country. Vidhya's core audience
// is international, so an unknown student country defaults to international.
function isInternationalFor(u, profile) {
  const uniCountry = String(u?.country || "United States").toLowerCase();
  const sc = String(profile?.student_country || profile?.citizenship || profile?.country || "").toLowerCase();
  const reUS = /(united states|usa|u\.s|america)/;
  if (!sc) return true;
  if (reUS.test(uniCountry)) return !reUS.test(sc);
  return sc !== uniCountry;
}
function pickTuitionTier(value, isIntl) {
  if (!value) return value;
  // Schools phrase the in-state tier many ways: "In-State", "<State> Resident"
  // (Georgia Resident), "In-District", "Domestic". Out-of-state: "Out-of-State",
  // "Non-Resident". Show only the tier relevant to the student.
  const inRe = /(?:\b[A-Z][a-zA-Z]+ Resident\b|In-?State|In-?District|\bDomestic\b)/i;
  const outRe = /(?:Out-?of-?State|Non-?Resident)/i;
  const inIdx = value.search(inRe);
  const outIdx = value.search(outRe);
  if (inIdx === -1 || outIdx === -1) return value;
  const clean = (t) => (t || "").trim().replace(/^[\s:;,.–-]+/, "").replace(/[\s:;,.]+$/, "");
  const lead = clean(value.slice(0, Math.min(inIdx, outIdx)));
  const wrap = (clause, re, label) => {
    const body = lead ? `${lead} — ${clause}` : clause;
    return re.test(clause.slice(0, 20)) ? body : `${label} — ${body}`;
  };
  if (isIntl) {
    const end = inIdx > outIdx ? inIdx : value.length;
    return wrap(clean(value.slice(outIdx, end)), outRe, "Out-of-State");
  }
  const end = outIdx > inIdx ? outIdx : value.length;
  return wrap(clean(value.slice(inIdx, end)), inRe, "In-State");
}
function TuitionFigure({ u, profile }) {
  const transform = (val) => pickTuitionTier(val, isInternationalFor(u, profile));
  return <FactFigure u={u} factType="tuition" title="Tuition (official)" cacheKey="tuition_verification" endpoint="verify-tuition" transformValue={transform} />;
}

// ───────────────────────── Costs & Aid ─────────────────────────
function CostsTab({ u, profile, computeCostAndAid, switchTab, isNavigator }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [missing, setMissing] = useState(null);
  const ca = u.cost_aid || null;

  const onCompute = async () => {
    setBusy(true); setErr(""); setMissing(null);
    try {
      await computeCostAndAid(u.id);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "profile_incomplete") {
        setMissing(detail);
      } else {
        setErr(formatApiError(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const items = [
    { label: "Living cost (est.)", v: u.living_cost || "—" },
    { label: "Total estimate / yr", v: u.total_cost_estimate || "—" },
    { label: "Application fee", v: u.application_fee || "—" },
    { label: "Your budget", v: profile?.budget || "—" },
    { label: "Acceptance rate", v: u.acceptRate || "—" },
  ];
  const schols = Array.isArray(u.scholarships_at_uni) ? u.scholarships_at_uni : [];

  const goFindScholarships = () => {
    if (ca?.scholarship_keywords?.length) {
      try {
        sessionStorage.setItem(
          "vidhya_schol_prefill",
          JSON.stringify({ keywords: ca.scholarship_keywords.join(", "), notes: ca.aid_strategy || "" })
        );
      } catch (e) { /* ignore */ }
    }
    window.location.href = "/app/scholarships";
  };

  return (
    <div className="col" style={{ gap: 16 }} data-testid="costs-tab">
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>Cost snapshot</h3>
        <TuitionFigure u={u} profile={profile} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginTop: 16 }}>
          {items.map((it) => (
            <div key={it.label}>
              <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>{it.label}</div>
              <div style={{ fontWeight: 600, marginTop: 4, fontSize: 16 }}>{it.v}</div>
            </div>
          ))}
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 16, lineHeight: 1.55 }}>
          Tuition is pulled live and verified against the official source. Living cost, totals and
          fees are estimates that depend on housing, lifestyle, and exchange rates — confirm them on
          the official page.
        </div>
      </div>

      <AdmitAidCard
        ca={ca} busy={busy} err={err} missing={missing}
        onCompute={onCompute} goFindScholarships={goFindScholarships}
        budget={profile?.budget}
        isNavigator={isNavigator}
      />

      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>Internal scholarships at this university</h3>
        {schols.length === 0 ? (
          u.research_status === "pending" ? (
            <div className="empty-state" style={{ fontSize: 13 }} data-testid="schols-internal-pending">
              We're still pulling internal scholarship data. Check back in a minute.
            </div>
          ) : (
            <div className="empty-state" style={{ fontSize: 13 }} data-testid="schols-internal-none">
              No university-specific scholarships were found for this program yet. Use “Find scholarships” above to surface external awards you qualify for.
            </div>
          )
        ) : (
          <ul style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 13.5, lineHeight: 1.6 }} data-testid="schols-internal">
            {schols.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        )}
        <button
          className="btn btn-sm"
          style={{ marginTop: 14 }}
          onClick={() => switchTab && switchTab("checklist")}
          data-testid="costs-jump-checklist"
        >
          <I.ArrowRight size={11} /> View application requirements
        </button>
      </div>
    </div>
  );
}

const COMPUTE_STEPS = [
  "Calculating your profile score",
  "Checking program requirements",
  "Cross-analyzing your profile against admitted averages",
];

function ComputeSteps({ active }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (!active) { setStep(0); return; }
    setStep(0);
    const timers = [
      setTimeout(() => setStep(1), 2600),
      setTimeout(() => setStep(2), 6400),
    ];
    return () => timers.forEach(clearTimeout);
  }, [active]);
  if (!active) return null;
  return (
    <div className="compute-steps" data-testid="compute-steps">
      {COMPUTE_STEPS.map((label, i) => {
        const state = i < step ? "done" : i === step ? "active" : "pending";
        return (
          <div key={i} className={`compute-step ${state}`} data-testid={`compute-step-${i}`}>
            <span className="cs-ico">
              {state === "done" ? <I.Check size={12} />
                : state === "active" ? <Loader2 size={12} className="vidhya-spin" />
                : <span className="cs-dot" />}
            </span>
            <span className="cs-label">{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function AdmitAidCard({ ca, busy, err, missing, onCompute, goFindScholarships, budget, isNavigator }) {
  // Plan gate FIRST: Explorer always sees the locked card, regardless of profile state
  // or cached cost_aid data (Navigator users who downgrade fall back here too).
  if (!isNavigator) {
    return (
      <LockedCard
        feature={FEATURES.AID_GAP}
        context="costs_tab_locked"
        title="Fit · admit chance · aid gap"
        description="See your honest admission odds, the funding gap to close, and a tailored aid strategy — once you upgrade."
        ctaLabel="Unlock with Navigator"
        testid="admit-aid-locked-plan"
      >
        <div style={{ padding: 18 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: 44, fontWeight: 600, color: "var(--ink-3)" }}>
            ──%
          </div>
          <div className="muted" style={{ fontSize: 12 }}>Admit chance preview</div>
        </div>
      </LockedCard>
    );
  }

  if (missing && !ca) {
    return (
      <div className="card" style={{ padding: 20 }} data-testid="admit-aid-locked">
        <h3 style={{ marginTop: 0 }}>Admit chance & aid gap</h3>
        <p style={{ fontSize: 13.5, lineHeight: 1.55, marginTop: 4, marginBottom: 12, color: "var(--ink-2)" }}>
          {missing.message || "We need a few more profile details to estimate this honestly."}
        </p>
        <div style={{ background: "rgba(184,138,58,0.07)", borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
          <div className="muted" style={{ fontSize: 11, fontFamily: "var(--font-mono)", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 6 }}>
            Fill in
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.6 }} data-testid="admit-aid-missing">
            {(missing.missing || []).map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
        <button
          className="btn btn-clay"
          onClick={() => { window.location.href = "/app/profile"; }}
          data-testid="admit-aid-complete-profile-btn"
        >
          <I.ArrowRight size={12} /> Complete profile to unlock
        </button>
      </div>
    );
  }
  if (!ca) {
    return (
      <div className="card" style={{ padding: 20 }} data-testid="admit-aid-empty">
        <h3 style={{ marginTop: 0 }}>Admit chance & aid gap</h3>
        <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55, marginTop: 4 }}>
          Get an honest read on your odds and the funding gap to close, based on your profile and this program's numbers.
        </p>
        {err && <div className="auth-error" style={{ marginBottom: 10 }} data-testid="admit-aid-error">{err}</div>}
        {busy ? (
          <ComputeSteps active={busy} />
        ) : (
          <button className="btn btn-clay" onClick={onCompute} disabled={busy} data-testid="admit-aid-compute-btn">
            <I.Sparkle size={12} /> Calculate admit chance & aid gap
          </button>
        )}
      </div>
    );
  }

  const band = (ca.admit_chance_band || "moderate").toLowerCase();
  const BAND = {
    low:      { fg: "#8a3c20", bg: "rgba(184,90,61,0.15)",  label: "Long shot" },
    moderate: { fg: "#B8893A", bg: "rgba(184,138,58,0.15)", label: "Realistic" },
    strong:   { fg: "#3a5a36", bg: "rgba(79,122,74,0.15)",  label: "Strong match" },
  };
  const b = BAND[band] || BAND.moderate;
  const pct = ca.admit_chance_pct;
  const gap = ca.aid_gap_usd || 0;
  const total = ca.estimated_total_cost_usd || 0;
  const cap = ca.budget_capacity_usd || 0;
  const internal = ca.internal_aid_estimate_usd || 0;

  return (
    <div className="card" style={{ padding: 20 }} data-testid="admit-aid-card">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 4, flexWrap: "wrap" }}>
        <h3 style={{ marginTop: 0 }}>Admit chance & aid gap</h3>
      </div>

      {busy && <ComputeSteps active={busy} />}

      {/* Admit chance gauge */}
      <div className="admit-gauge" data-testid="admit-gauge">
        <div className="admit-gauge-row">
          <div className="admit-gauge-pct" style={{ color: b.fg }}>{pct}%</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
            <span
              style={{
                background: b.bg, color: b.fg, fontSize: 10.5, fontFamily: "var(--font-mono)",
                letterSpacing: "0.08em", textTransform: "uppercase", padding: "3px 8px",
                borderRadius: 999, fontWeight: 600, alignSelf: "flex-start",
              }}
              data-testid={`admit-band-${band}`}
            >
              {b.label}
            </span>
            <div style={{ fontSize: 13.5, lineHeight: 1.5, color: "var(--ink-2)" }} data-testid="admit-reasoning">
              {ca.admit_chance_reasoning}
            </div>
          </div>
        </div>
        <div className="admit-gauge-bar">
          <div
            className="admit-gauge-fill"
            style={{ width: `${pct}%`, background: b.fg }}
          />
        </div>
      </div>

      {/* Aid gap stack */}
      <div style={{ marginTop: 18 }}>
        <div className="muted" style={{ fontSize: 11, fontFamily: "var(--font-mono)", letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>
          Aid gap to close
        </div>
        <div className="aid-stack">
          <AidRow label="Estimated total / yr" v={`$${total.toLocaleString()}`} />
          <AidRow label={`Your budget${budget ? ` (${budget})` : ""}`} v={`-$${cap.toLocaleString()}`} dim />
          <AidRow label="Likely internal aid" v={`-$${internal.toLocaleString()}`} dim />
          <AidRow label="Gap to close" v={`$${gap.toLocaleString()}`} bold danger={gap > 0} />
        </div>
      </div>

      {ca.aid_strategy && (
        <div style={{ marginTop: 14, padding: "12px 14px", background: "rgba(184,138,58,0.07)", borderRadius: 10, fontSize: 13.5, lineHeight: 1.55 }}>
          <strong style={{ fontSize: 11, fontFamily: "var(--font-mono)", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)", display: "block", marginBottom: 4 }}>
            Strategy
          </strong>
          {ca.aid_strategy}
        </div>
      )}

      {gap > 0 && (
        <button
          className="btn btn-clay"
          style={{ marginTop: 14 }}
          onClick={goFindScholarships}
          data-testid="find-scholarships-btn"
        >
          <I.Trophy size={12} /> Find scholarships to close the gap
        </button>
      )}

      {Array.isArray(ca.caveats) && ca.caveats.length > 0 && (
        <ul className="muted" style={{ marginTop: 14, paddingLeft: 18, fontSize: 12, lineHeight: 1.55 }} data-testid="admit-caveats">
          {ca.caveats.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      )}
    </div>
  );
}

function AidRow({ label, v, dim, bold, danger }) {
  return (
    <div className="aid-row">
      <span style={{ color: dim ? "var(--ink-3)" : "var(--ink-2)", fontSize: 13 }}>{label}</span>
      <span style={{
        fontWeight: bold ? 700 : 500,
        fontSize: bold ? 18 : 14,
        color: danger ? "#8a3c20" : "var(--ink)",
      }}>{v}</span>
    </div>
  );
}

// ───────────────────────── Professors ─────────────────────────
function ProfessorsTab({
  u, profs, profsBusy, profsErr,
  interest, setInterest, department, setDepartment,
  findProfs, draftWithAi, isNavigator,
}) {
  if (!isNavigator) {
    return (
      <LockedCard
        feature={FEATURES.PROF_EXTRACT}
        context="detail_professors_tab"
        title="Professor outreach unlocks with Navigator"
        description="Pull real faculty emails, labs, and research areas for this university — plus AI-drafted cold emails grounded in your background."
        ctaLabel="Unlock with Navigator"
        testid="profs-locked-card"
      >
        <div style={{ padding: 18, opacity: 0.5 }}>
          <div className="prof-row" style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div className="prof-avatar" style={{ background: u.color, color: "var(--bg-elevated)" }}>??</div>
            <div>
              <div className="prof-name">Professor ████████</div>
              <div className="prof-research">Research area hidden</div>
            </div>
          </div>
        </div>
      </LockedCard>
    );
  }
  return (
    <div className="card contacts" data-testid="profs-card" style={{ padding: 20 }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <h3>Professors <span className="pill" style={{ marginLeft: 8 }}>{profs.length} found</span></h3>
      </div>
      <div className="row" style={{ marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
        <input
          className="input"
          style={{ flex: "1 1 180px", minWidth: 160 }}
          placeholder="Department / school (e.g. Mechanical Engineering)"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          data-testid="profs-department-input"
        />
      </div>
      <div className="row" style={{ marginBottom: 12 }}>
        <input
          className="input"
          style={{ flex: 1 }}
          placeholder="Research interest (e.g. soft robotics, ocean modeling)"
          value={interest}
          onChange={(e) => setInterest(e.target.value)}
          data-testid="profs-interest-input"
        />
        <button className="btn btn-sm btn-clay" onClick={findProfs} disabled={profsBusy} data-testid="profs-find-btn">
          {profsBusy ? "Finding…" : <><I.Sparkle size={11} /> Find with AI</>}
        </button>
      </div>
      {profsErr && <div className="auth-error">{profsErr}</div>}
      {profs.length === 0 && !profsBusy && (
        <div className="empty-state" style={{ fontSize: 12.5 }}>
          Click "Find with AI", we'll pull live faculty matches from official sources.
        </div>
      )}
      <div className="prof-list">
        {profs.map((p, i) => (
          <div className="prof-row" key={i}>
            <div className="prof-avatar" style={{ background: u.color, color: "var(--bg-elevated)" }}>
              {(p.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
            <div>
              <div className="prof-name">{p.name}</div>
              {p.department && (
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 3 }}>
                  {p.department}
                </div>
              )}
              <div className="prof-research">{p.research}</div>
              {p.notable_work && (
                <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 4, fontStyle: "italic" }}>
                  ↗ {p.notable_work}
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
              {p.match != null && <div className="prof-match">{p.match}% match</div>}
              <button className="btn btn-sm" onClick={() => draftWithAi(p)} data-testid={`draft-email-${i}`}>
                <I.Mail size={11} /> Draft email
              </button>
              {p.lab_url && (
                <a href={p.lab_url} target="_blank" rel="noreferrer" className="btn btn-sm btn-ghost" style={{ fontSize: 11 }}>
                  Lab page →
                </a>
              )}
              {p.lab_url && p.verified && (
                <VerifiedBadge status="verified" source={p.source || p.lab_url} compact />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────── Email Drawer (slide-in panel) ─────────────────────────
// Wraps the existing EmailsTab in a right-side slide-in drawer. Lives on the uni
// page so all drafts/history are saved to this university (no in-page section).
function EmailDrawer({ open, onClose, ...props }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  return (
    <>
      <div className={`email-drawer-scrim ${open ? "show" : ""}`} onClick={onClose} data-testid="email-drawer-scrim" />
      <aside className={`email-drawer ${open ? "show" : ""}`} data-testid="email-drawer" aria-label="Outreach drawer" aria-hidden={!open}>
        <div className="ed-head">
          <div className="ed-title">
            <I.Mail size={14} />
            <div>
              <b>Outreach for {props.u?.name}</b>
              <span>Saved to this university</span>
            </div>
          </div>
          <button className="ed-close" onClick={onClose} aria-label="Close outreach drawer" data-testid="email-drawer-close">
            <I.Close size={14} />
          </button>
        </div>
        <div className="ed-body">
          <EmailsTab {...props} />
        </div>
      </aside>
    </>
  );
}

// ───────────────────────── Emails ─────────────────────────
function EmailsTab({
  u, gmail, drafting, profs, emailDraft, setEmailDraft,
  gmailBusy, gmailMsg, draftWithAi, saveDraft, openGmailConsent,
  saveToGmailDrafts, sendNow, emailHistory = [], isNavigator,
}) {
  if (!isNavigator) {
    return (
      <div className="col" style={{ gap: 16 }}>
        <LockedCard
          feature={FEATURES.GMAIL}
          context="detail_emails_tab"
          title="Gmail outreach unlocks with Navigator"
          description="Connect Gmail to draft personalised professor emails, save them straight to your inbox, and one-click send — all logged inside Vidhya."
          ctaLabel="Unlock with Navigator"
          testid="emails-locked-card"
        >
          <div style={{ padding: 18 }}>
            <div className="email-form" style={{ pointerEvents: "none", opacity: 0.55 }}>
              <div className="email-row">
                <label>To</label>
                <input value="prof.████████@university.edu" readOnly />
              </div>
              <div className="email-row">
                <label>Subject</label>
                <input value="████████████████████████" readOnly />
              </div>
            </div>
          </div>
        </LockedCard>
      </div>
    );
  }
  return (
    <div className="col" style={{ gap: 16 }}>
    <div className="card composer-card" data-testid="email-composer" style={{ padding: 20 }}>
      <h3>Email composer</h3>
      <div className="ai-draft-banner">
        <I.Sparkle size={14} />
        <span>Drafts are personalized using the professor's research and your background.</span>
        <button className="btn btn-sm btn-clay" style={{ marginLeft: "auto" }} onClick={() => draftWithAi()} disabled={drafting || profs.length === 0} data-testid="composer-draft-btn">
          {drafting ? "Drafting…" : "Draft with AI"}
        </button>
      </div>
      <div className="email-form">
        <div className="email-row">
          <label>To</label>
          <input value={emailDraft.to} onChange={(e) => setEmailDraft((d) => ({ ...d, to: e.target.value }))} placeholder="professor@university.edu" />
        </div>
        <div className="email-row">
          <label>Subject</label>
          <input value={emailDraft.subject} onChange={(e) => setEmailDraft((d) => ({ ...d, subject: e.target.value }))} placeholder="Subject" />
        </div>
        <textarea
          className="email-body-input"
          value={emailDraft.body}
          onChange={(e) => setEmailDraft((d) => ({ ...d, body: e.target.value }))}
          placeholder="Click 'Draft with AI' or write your message…"
          data-testid="email-body-input"
        />
        <div className="composer-actions">
          {gmailMsg && (
            <div className="composer-msg" data-testid="gmail-msg">{gmailMsg}</div>
          )}
          <div className="row" style={{ paddingTop: 8, borderTop: "1px solid var(--ink-5)", flexWrap: "wrap", gap: 8 }}>
            <div className="muted" style={{ fontSize: 11.5 }}>
              {gmail?.connected ? (
                <><I.Mail size={12} /> Gmail connected: <strong>{gmail.email}</strong></>
              ) : (
                <><I.Mail size={12} /> Connect Gmail to save real drafts &amp; send from your inbox</>
              )}
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
              <CopyEmailButton draft={emailDraft} />
              <button className="btn btn-sm" onClick={saveDraft} disabled={!emailDraft.body.trim()} data-testid="email-save-btn">
                Save locally
              </button>
              {gmail?.connected ? (
                <>
                  <button
                    className="btn btn-sm"
                    onClick={saveToGmailDrafts}
                    disabled={gmailBusy === "draft" || !emailDraft.body.trim()}
                    data-testid="gmail-draft-btn"
                  >
                    {gmailBusy === "draft" ? "Saving…" : "Save to Gmail drafts"}
                  </button>
                  <button
                    className="btn btn-sm btn-clay"
                    onClick={sendNow}
                    disabled={gmailBusy === "send" || !emailDraft.body.trim()}
                    data-testid="gmail-send-btn"
                  >
                    {gmailBusy === "send" ? "Sending…" : "Send via Gmail"}
                  </button>
                </>
              ) : (
                <button className="btn btn-sm btn-clay" onClick={openGmailConsent} data-testid="gmail-connect-btn">
                  Connect Gmail
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    {emailHistory.length > 0 && (
      <div className="card" data-testid="email-history" style={{ padding: 20 }}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Past emails</h3>
          <span className="pill">{emailHistory.length} saved</span>
        </div>
        <div className="col" style={{ gap: 10 }}>
          {[...emailHistory]
            .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
            .map((m) => (
              <EmailHistoryRow
                key={m.id}
                m={m}
                onLoadDraft={() => setEmailDraft({
                  to: m.to || "",
                  subject: m.subject || "",
                  body: m.body || "",
                  professor_name: m.professor_name || m.professor || "",
                })}
              />
            ))}
        </div>
      </div>
    )}
    </div>
  );
}

function EmailHistoryRow({ m, onLoadDraft }) {
  const [open, setOpen] = useState(false);
  const when = m.created_at ? new Date(m.created_at).toLocaleString() : "";
  return (
    <div className="email-history-row" data-testid={`email-history-${m.id}`}>
      <div className="row" style={{ justifyContent: "space-between", gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {m.subject || "(no subject)"}
          </div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
            To {m.to || "—"} {m.professor_name || m.professor ? `· ${m.professor_name || m.professor}` : ""} · {when}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button className="btn btn-sm btn-ghost" onClick={() => setOpen((v) => !v)} data-testid={`email-history-toggle-${m.id}`}>
            {open ? "Hide" : "View"}
          </button>
          <button className="btn btn-sm" onClick={onLoadDraft} title="Load into composer" data-testid={`email-history-reuse-${m.id}`}>
            <I.Copy size={11} /> Reuse
          </button>
        </div>
      </div>
      {open && (
        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "var(--font-body)", fontSize: 13, marginTop: 10, color: "var(--ink-2)" }}>
          {m.body || "(empty)"}
        </pre>
      )}
    </div>
  );
}

// ───────────────────────── Notes ─────────────────────────
function NotesTab({ u, notes, setNotes, notesSaving, notesSavedAt }) {
  return (
    <div className="card notepad-card" data-testid="university-notepad" style={{ padding: 20 }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <h3>Notes</h3>
        <span className="pill" style={{ fontSize: 10 }}>
          {notesSaving ? "Saving…" : notesSavedAt ? "✓ Saved" : "Auto-saves"}
        </span>
      </div>
      <textarea
        className="input notepad-textarea"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={`Capture your thoughts on ${u.name}: campus visit observations, conversations with current students, why this school resonates, concerns…`}
        rows={10}
        data-testid="notepad-textarea"
      />
      <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
        Vidhya's AI advisor reads these notes when you ask program-specific questions.
      </div>
    </div>
  );
}

// ───────────────────────── Shared widgets ─────────────────────────
function Stat({ label, v, urgent }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ fontWeight: 600, marginTop: 2, color: urgent ? "var(--red)" : "inherit" }}>{v}</div>
    </div>
  );
}

function Modal({ children, onClose, title, testid }) {
  return (
    <div className="modal-back" onClick={onClose} data-testid={testid}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ justifyContent: "space-between", marginBottom: 12 }}>
          <h4 style={{ margin: 0 }}>{title}</h4>
          <button className="btn btn-sm btn-ghost" onClick={onClose}><I.Close size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const TIER_STYLE = {
  reach:   { bg: "rgba(184,90,61,0.15)",  fg: "#8a3c20", label: "Ambitious" },
  target:  { bg: "rgba(79,122,74,0.15)",  fg: "#3a5a36", label: "Good match" },
  safety:  { bg: "rgba(74,111,138,0.15)", fg: "#345469", label: "Backup" },
};

function FitSnapshot({ u, isResearching }) {
  const tier = (u.tier || "").toLowerCase();
  const t = TIER_STYLE[tier];
  const highlights = Array.isArray(u.highlights) ? u.highlights : [];
  const gaps = Array.isArray(u.gaps) ? u.gaps : [];
  const schols = Array.isArray(u.scholarships_at_uni) ? u.scholarships_at_uni : [];
  const hasAnything = t || u.fit_notes || highlights.length || gaps.length || schols.length
    || u.department || u.program_length || u.english_min || u.gpa_required || u.official_url;

  if (!hasAnything && isResearching) {
    return (
      <div className="card" data-testid="fit-snapshot-loading" style={{ padding: "18px 20px" }}>
        <div className="row" style={{ alignItems: "center", gap: 10 }}>
          <I.Sparkle size={14} />
          <span style={{ fontSize: 13 }}>Researching this program for you — fit notes, costs, requirements…</span>
        </div>
        <div className="skeleton-line" style={{ marginTop: 14 }} />
        <div className="skeleton-line" style={{ marginTop: 8, width: "70%" }} />
      </div>
    );
  }

  if (!hasAnything) return null;

  return (
    <div className="card" data-testid="fit-snapshot" style={{ padding: "18px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)" }}>
              Fit snapshot — tailored to your profile
            </span>
            {t && (
              <span
                style={{
                  background: t.bg, color: t.fg, fontSize: 10.5, fontFamily: "var(--font-mono)",
                  letterSpacing: "0.08em", textTransform: "uppercase", padding: "3px 8px",
                  borderRadius: 999, fontWeight: 600,
                }}
                data-testid={`tier-${tier}`}
              >
                {t.label}
              </span>
            )}
          </div>
          {u.fit_notes && <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: "var(--ink)" }} data-testid="fit-notes">{u.fit_notes}</p>}
        </div>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 12.5, color: "var(--ink-2)" }}>
          {u.department && <MiniField label="Dept" v={u.department} />}
          {u.program_length && <MiniField label="Length" v={u.program_length} />}
          {u.gpa_required && <MiniField label="GPA" v={u.gpa_required} />}
          {u.english_min && <MiniField label="English" v={u.english_min} />}
        </div>
      </div>

      {(highlights.length > 0 || gaps.length > 0 || schols.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginTop: 14 }}>
          {highlights.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 6 }}>
                Why this program
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }} data-testid="highlights-list">
                {highlights.map((h, i) => <li key={`h-${i}`} style={{ marginBottom: 3 }}>{h}</li>)}
              </ul>
            </div>
          )}
          {gaps.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", letterSpacing: "0.14em", textTransform: "uppercase", color: "#8a3c20", marginBottom: 6 }}>
                To strengthen for a real shot
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }} data-testid="gaps-list">
                {gaps.map((g, i) => <li key={`g-${i}`} style={{ marginBottom: 3 }}>{g}</li>)}
              </ul>
            </div>
          )}
          {schols.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontFamily: "var(--font-mono)", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 6 }}>
                Internal scholarships
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.5 }} data-testid="schols-list">
                {schols.map((s, i) => <li key={`s-${i}`} style={{ marginBottom: 3 }}>{s}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {u.official_url && (
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <a
            href={u.official_url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-ghost"
            data-testid="official-program-link"
            style={{ textDecoration: "none" }}
          >
            Official program page →
          </a>
          <VerifiedBadge
            status={u.verification?.official_url?.status === "verified" ? "verified" : "unverified"}
            source={u.verification?.official_url?.source_url || u.official_url}
            checkedAt={u.verification?.official_url?.fetched_at || u.verification?.checked_at}
          />
          <span className="muted" style={{ fontSize: 11 }}>(link only — figures verified separately)</span>
        </div>
      )}
    </div>
  );
}

function MiniField({ label, v }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontFamily: "var(--font-mono)", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-3)" }}>{label}</div>
      <div style={{ fontWeight: 600, marginTop: 2 }}>{v}</div>
    </div>
  );
}

function CopyEmailButton({ draft }) {
  const [copied, setCopied] = useState(false);
  const disabled = !(draft?.body?.trim()) && !(draft?.subject?.trim()) && !(draft?.to?.trim());

  const onCopy = async () => {
    const payload =
      `To: ${draft.to || ""}\n` +
      `Subject: ${draft.subject || ""}\n` +
      `\n${draft.body || ""}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(payload);
      } else {
        const ta = document.createElement("textarea");
        ta.value = payload;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.error("[email] copy failed:", err);
    }
  };

  return (
    <button
      type="button"
      className={`btn btn-sm copy-btn ${copied ? "copied" : ""}`}
      onClick={onCopy}
      disabled={disabled}
      title="Copy recipient, subject and body in one go"
      data-testid="email-copy-btn"
    >
      <I.Copy size={12} /> {copied ? "Copied" : "Copy email"}
    </button>
  );
}
